/**
 * The integrations platform beyond incoming webhooks (docs/features/integrations.md), modelled on Slack's:
 *
 * - **Apps** (Settings → Integrations → Apps): owners and admins create an app in their workspace. It gets a bot user
 *   (a `Member` of kind "agent" with `agent.appId`), a bot token (`bxb-…`, shown once, stored hashed), a client id /
 *   client secret (OAuth; the secret is shown once, stored hashed) and a signing secret (signs every request Bundle
 *   sends the app). Other workspaces install it through OAuth 2 (`/oauth/authorize` → code → `oauth.v2.access`), which
 *   creates their own bot user and token. `auth.revoke` revokes a token.
 * - **Web API** (`POST /api/<method>`, Slack-style `{ ok, … }` answers): `chat.postMessage`, `chat.update`,
 *   `conversations.list`, `conversations.history`, `users.info`, `reactions.add`, each behind a scope and a
 *   per-app, per-method rate limit (429 + `Retry-After`).
 * - **Slash commands** an app registers (name, request URL, description, usage hint) show in the composer's `/` menu
 *   next to the built-ins. Running one POSTs the app a signed form (Slack's fields + `response_url`); the answer is
 *   ephemeral ("Only visible to you") or posted in the channel, now or later through the response_url.
 * - **Interactivity**: a Block Kit button POSTs the app's interactivity URL a signed `block_actions` payload.
 * - **Outgoing webhooks**: new messages in chosen channels, or starting with a trigger word, are POSTed (signed) to a
 *   URL through the queue with retries; a `{ "text" }` answer is posted back as the webhook.
 *
 * Signing (Slack's scheme, our header names): `X-Bundle-Request-Timestamp: <unix seconds>` and
 * `X-Bundle-Signature: v0=<hex HMAC-SHA256(secret, "v0:" + timestamp + ":" + raw body)>`.
 */
import { z } from "zod";
import { KitBlocks, KitHomeView, KitOption, KitOptionGroup, KitStateValue, KitStateValues, KitView } from "./blockKit.ts";
import { AppAuthor, Id, Message, Timestamp } from "./entities.ts";
import { AppStepOutput, VariableKey, WorkflowVarType } from "./workflows.ts";

// ---------- Apps ----------

/**
 * Scopes (Slack's names). `commands` lets the app register slash commands; the `*:history`, `groups:read`,
 * `app_mentions:read` and `reactions:read` scopes are for Events API subscriptions (core appEvents.ts).
 */
export const AppScope = z.enum([
  "chat:write", "channels:read", "channels:history", "users:read", "reactions:write", "commands",
  "groups:read", "groups:history", "im:history", "mpim:history", "app_mentions:read", "reactions:read",
]);
export type AppScope = z.infer<typeof AppScope>;

/**
 * Events API bot events an app can subscribe to (Slack's names). Conversation events only reach apps whose bot is in
 * the conversation; `channel_created` / `channel_rename` cover every public channel. Each needs a scope
 * (core `appEventScope`). `app_home_opened` needs no subscription.
 */
export const AppEventType = z.enum([
  "message.channels", "message.groups", "message.im", "message.mpim", "app_mention", "reaction_added", "reaction_removed",
  "member_joined_channel", "member_left_channel", "channel_created", "channel_rename",
]);
export type AppEventType = z.infer<typeof AppEventType>;

/** Apps a workspace can make (installed ones from elsewhere don't count). */
export const MAX_APPS = 50;
/** Slash commands per app. */
export const MAX_APP_COMMANDS = 25;

/** "/deploy": a slash, then 1–31 lowercase letters, digits, - or _. */
export const SlashCommandName = z.string().regex(/^\/[a-z0-9_-]{1,31}$/);

export const AppCommand = z.object({
  command: SlashCommandName,
  /** Request URL (https; http only for localhost). */
  url: z.string().url().max(2048),
  description: z.string().trim().min(1).max(100),
  /** Shown after the command in the menu, e.g. "[environment]". */
  usageHint: z.string().trim().max(100).optional(),
});
export type AppCommand = z.infer<typeof AppCommand>;

/** Workflow steps an app declares (Slack's custom functions / Workflow Steps). */
export const MAX_APP_WORKFLOW_STEPS = 20;
/** Inputs (and outputs) per declared step. */
export const MAX_APP_STEP_PARAMS = 20;

/** "create_ticket": letters, digits, _, - and . (Slack's function callback_id), 1–64. */
export const AppStepCallbackId = z.string().regex(/^[A-Za-z0-9_.-]{1,64}$/);

/** One input a declared step takes: the key its `inputs` record uses, what the builder shows, and its type. */
export const AppStepInput = z.object({
  name: VariableKey,
  label: z.string().trim().min(1).max(100),
  type: WorkflowVarType,
  /** The builder won't save the step while it's empty. */
  required: z.boolean().optional(),
});
export type AppStepInput = z.infer<typeof AppStepInput>;

/**
 * A workflow step the app offers (Slack's function: callback_id, title, description, input/output parameters). The
 * Workflow Builder lists it under the app in every workspace the app is installed in; picking it makes an `app_step`
 * (contracts workflows.ts) with these inputs (empty templates) and outputs (core `appStepFromDeclaration`). Runs use
 * the step's own copy, so changing a declaration doesn't change saved workflows.
 */
export const AppWorkflowStep = z.object({
  callbackId: AppStepCallbackId,
  title: z.string().trim().min(1).max(80),
  description: z.string().trim().max(200).optional(),
  inputs: z.array(AppStepInput).max(MAX_APP_STEP_PARAMS),
  outputs: z.array(AppStepOutput).max(MAX_APP_STEP_PARAMS),
});
export type AppWorkflowStep = z.infer<typeof AppWorkflowStep>;

export const App = z.object({
  id: Id,
  name: z.string().min(1).max(80),
  description: z.string().max(500).optional(),
  iconUrl: z.string().optional(),
  /** The workspace that made it; its owners and admins manage it. */
  ownerWorkspaceId: Id,
  /** Installed here from another workspace through OAuth (managed there; here it can only be removed). */
  external: z.boolean(),
  /** Its bot user in this workspace. */
  botUserId: Id,
  /** OAuth client id (public). */
  clientId: z.string(),
  /** Scopes granted in this workspace. */
  scopes: z.array(AppScope),
  /** OAuth redirect URLs (owner workspace only; empty for external apps). */
  redirectUrls: z.array(z.string()),
  /** Where block_actions payloads go (owner workspace only). */
  interactivityUrl: z.string().optional(),
  /** Slack's "Options Load URL": external_select queries (`block_suggestion`) go here (owner workspace only). */
  optionsUrl: z.string().optional(),
  /** Slack's Event Subscriptions "Request URL": events such as `app_home_opened` go here (owner workspace only). */
  eventsUrl: z.string().optional(),
  /** Subscribed bot events (owner workspace only; absent elsewhere and on older servers). */
  events: z.array(AppEventType).optional(),
  /**
   * The last time Bundle stopped sending this app events for 60 s because it kept answering its own bot's posts (more
   * than 10 such events a second; core `selfEventCheck`). Owner workspace only; Settings → Apps warns for a day.
   */
  eventsLoopAt: Timestamp.optional(),
  commands: z.array(AppCommand),
  /** Workflow steps it offers (every workspace it's installed in; absent on older servers). */
  workflowSteps: z.array(AppWorkflowStep).optional(),
  createdBy: Id,
  createdAt: Timestamp,
  /** When it was installed in this workspace. */
  installedAt: Timestamp,
});
export type App = z.infer<typeof App>;

/** GET /v1/w/:ws/apps — apps made here and apps installed here, newest first. */
export const ListAppsResponse = z.object({ apps: z.array(App) });
export type ListAppsResponse = z.infer<typeof ListAppsResponse>;

const url = z.string().url().max(2048);

/** POST /v1/w/:ws/apps — owners and admins. */
export const CreateAppRequest = z.object({
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().max(500).optional(),
  scopes: z.array(AppScope).max(AppScope.options.length),
  redirectUrls: z.array(url).max(10).optional(),
  interactivityUrl: url.optional(),
  optionsUrl: url.optional(),
  eventsUrl: url.optional(),
  /** Needs the events URL and the scopes each event needs (core `missingEventScopes`). */
  events: z.array(AppEventType).max(AppEventType.options.length).optional(),
  commands: z.array(AppCommand).max(MAX_APP_COMMANDS).optional(),
  /** Callback ids unique; input and output names unique within a step (core `appWorkflowStepsProblem`). */
  workflowSteps: z.array(AppWorkflowStep).max(MAX_APP_WORKFLOW_STEPS).optional(),
});
export type CreateAppRequest = z.infer<typeof CreateAppRequest>;

/** PATCH /v1/w/:ws/apps/:appId — apps made here. `interactivityUrl` / `optionsUrl` / `eventsUrl: null` clears it. */
export const UpdateAppRequest = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  description: z.string().trim().max(500).optional(),
  scopes: z.array(AppScope).max(AppScope.options.length).optional(),
  redirectUrls: z.array(url).max(10).optional(),
  interactivityUrl: url.nullable().optional(),
  optionsUrl: url.nullable().optional(),
  eventsUrl: url.nullable().optional(),
  events: z.array(AppEventType).max(AppEventType.options.length).optional(),
  commands: z.array(AppCommand).max(MAX_APP_COMMANDS).optional(),
  workflowSteps: z.array(AppWorkflowStep).max(MAX_APP_WORKFLOW_STEPS).optional(),
});
export type UpdateAppRequest = z.infer<typeof UpdateAppRequest>;

export const AppResponse = z.object({ app: App });
export type AppResponse = z.infer<typeof AppResponse>;

/** Create: the app and its credentials, each shown this once except the signing secret (GET …/signing-secret). */
export const CreateAppResponse = z.object({ app: App, botToken: z.string(), clientSecret: z.string(), signingSecret: z.string() });
export type CreateAppResponse = z.infer<typeof CreateAppResponse>;

export const AppSecretKind = z.enum(["botToken", "clientSecret", "signingSecret"]);
export type AppSecretKind = z.infer<typeof AppSecretKind>;

/** POST /v1/w/:ws/apps/:appId/secrets — a new credential (the old one stops working). */
export const RotateAppSecretRequest = z.object({ kind: AppSecretKind });
export type RotateAppSecretRequest = z.infer<typeof RotateAppSecretRequest>;

/** Rotate, or GET /v1/w/:ws/apps/:appId/signing-secret. */
export const AppSecretResponse = z.object({ kind: AppSecretKind, secret: z.string() });
export type AppSecretResponse = z.infer<typeof AppSecretResponse>;

// ---------- Modals (views) ----------

/** Most views in one modal stack (Slack: 3). */
export const MAX_VIEW_STACK = 3;

/** A view as the person's client gets it: the app's `KitView` with its ids. */
export const AppView = z.object({
  /** The view_id apps use (views.update). */
  id: z.string(),
  app: AppAuthor,
  view: KitView,
  /** Changes with every update (views.update's `hash`). */
  hash: z.string(),
  /** The conversation it was opened from (conversations_select's default_to_current_conversation). */
  channelId: Id.optional(),
});
export type AppView = z.infer<typeof AppView>;

/**
 * My open modal: its views, bottom first (the last one shows; up to 3). `views` empty = closed. `rootId` stays the same
 * while views are pushed, updated or popped. `clientToken` is the one sent with the interaction that opened it.
 */
export const ViewStack = z.object({ rootId: z.string(), views: z.array(AppView).max(MAX_VIEW_STACK), clientToken: z.string().optional() });
export type ViewStack = z.infer<typeof ViewStack>;

/** GET /v1/w/:ws/views — my open modal (after a reconnect), or none. */
export const CurrentViewsResponse = z.object({ views: ViewStack.optional() });
export type CurrentViewsResponse = z.infer<typeof CurrentViewsResponse>;

/** POST /v1/w/:ws/views/:viewId/submit — the top view's submit button (view_submission). */
export const ViewSubmitRequest = z.object({ values: KitStateValues });
export type ViewSubmitRequest = z.infer<typeof ViewSubmitRequest>;

/**
 * What the app answered: the stack now (closed, updated, pushed), or `errors` by block_id to show under the inputs
 * (`response_action: "errors"`, the view stays), or `error` when the app couldn't be reached ("http_500", "timeout").
 */
export const ViewSubmitResponse = z.object({ views: ViewStack, errors: z.record(z.string(), z.string()).optional(), error: z.string().optional() });
export type ViewSubmitResponse = z.infer<typeof ViewSubmitResponse>;

/** POST /v1/w/:ws/views/:viewId/close — Cancel / back (this view) or X / Escape (`all`: the whole stack). */
export const ViewCloseRequest = z.object({ all: z.boolean().optional() });
export type ViewCloseRequest = z.infer<typeof ViewCloseRequest>;

/** POST /v1/w/:ws/views/:viewId/actions — an element in a modal changed or was pressed (block_actions). */
export const ViewActionRequest = z.object({
  actionId: z.string().min(1).max(255),
  blockId: z.string().max(255),
  value: z.string().max(2000).optional(),
  selected: KitStateValue.optional(),
  values: KitStateValues,
});
export type ViewActionRequest = z.infer<typeof ViewActionRequest>;

// ---------- Pickers, external options, App Home ----------

/**
 * GET /v1/w/:ws/kit/options — the choices of a people / conversation / channel select, searched on the server a page
 * at a time (Slack's pickers search the whole workspace, not what a client has cached). `q` matches names (people:
 * display, full name; conversations: channel names, and DMs by the other people's names); `ids` instead returns those
 * ids (labels for chosen values). Conversations are the public channels and the private channels and DMs I'm in,
 * narrowed by `include`; channels are public channels only.
 */
export const KitOptionsQuery = z.object({
  source: z.enum(["users", "conversations", "channels"]),
  q: z.string().max(100).optional(),
  /** Comma-separated "im,mpim,private,public" (conversations; absent = all four, like Slack). */
  include: z.string().max(40).optional(),
  /** "true": people only, no bots / apps. */
  excludeBots: z.enum(["true", "false"]).optional(),
  /** Comma-separated ids to look up instead of searching (at most 100). */
  ids: z.string().max(3000).optional(),
  cursor: z.string().max(40).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type KitOptionsQuery = z.infer<typeof KitOptionsQuery>;

/** One choice of a server-searched picker: the id, its label and what it is (for the row's icon / avatar). */
export const KitPickerOption = z.object({
  value: Id,
  label: z.string(),
  kind: z.enum(["user", "bot", "public", "private", "im", "mpim"]),
});
export type KitPickerOption = z.infer<typeof KitPickerOption>;

export const KitOptionsResponse = z.object({ options: z.array(KitPickerOption), nextCursor: z.string().optional() });
export type KitOptionsResponse = z.infer<typeof KitOptionsResponse>;

/**
 * POST /v1/w/:ws/apps/options — an external_select's options for what the person typed: the server finds the element
 * on its message, modal or Home tab (exactly one of `messageId`, `viewId`, `homeAppId`) and asks the app (a signed
 * `block_suggestion` to its options load URL, 3 s). The app's `options` or `option_groups` come back checked.
 */
export const ExternalOptionsRequest = z.object({
  messageId: Id.optional(),
  viewId: z.string().max(64).optional(),
  homeAppId: Id.optional(),
  blockId: z.string().max(255),
  actionId: z.string().min(1).max(255),
  value: z.string().max(200),
});
export type ExternalOptionsRequest = z.infer<typeof ExternalOptionsRequest>;

export const ExternalOptionsResponse = z.object({
  options: z.array(KitOption).max(100),
  optionGroups: z.array(KitOptionGroup).max(100).optional(),
});
export type ExternalOptionsResponse = z.infer<typeof ExternalOptionsResponse>;

/** An app's Home tab for me (Slack's App Home): the view it published with `views.publish`, if any. */
export const AppHome = z.object({
  appId: Id,
  /** The app's bot user (its DM is where the tab shows). */
  botUserId: Id,
  app: AppAuthor,
  view: KitHomeView.optional(),
  viewId: z.string().optional(),
  hash: z.string().optional(),
});
export type AppHome = z.infer<typeof AppHome>;

/** GET /v1/w/:ws/apps/:appId/home — opening the tab also sends the app `app_home_opened` (Events API). */
export const AppHomeResponse = z.object({ home: AppHome });
export type AppHomeResponse = z.infer<typeof AppHomeResponse>;

/** POST /v1/w/:ws/apps/:appId/home/actions — an element in the Home tab changed or was pressed (block_actions). */
export const HomeActionRequest = ViewActionRequest.extend({});
export type HomeActionRequest = z.infer<typeof HomeActionRequest>;

/** Close: the stack now. */
export const ViewStackResponse = z.object({ views: ViewStack });
export type ViewStackResponse = z.infer<typeof ViewStackResponse>;

// ---------- Slash commands ----------

/** A command in the composer's `/` menu from an app installed here. */
export const AvailableCommand = z.object({
  command: SlashCommandName,
  description: z.string(),
  usageHint: z.string().optional(),
  appId: Id,
  appName: z.string(),
});
export type AvailableCommand = z.infer<typeof AvailableCommand>;

/** GET /v1/w/:ws/commands — app commands (the built-ins are core `SLASH_COMMANDS`). */
export const ListCommandsResponse = z.object({ commands: z.array(AvailableCommand) });
export type ListCommandsResponse = z.infer<typeof ListCommandsResponse>;

/** POST /v1/w/:ws/commands/run — `command` with its slash, `text` = everything after it. */
export const RunCommandRequest = z.object({
  channelId: Id,
  command: SlashCommandName,
  text: z.string().max(4000),
  threadRootId: Id.optional(),
  /** A random id this client made for the interaction: modals the app opens from it come back with it (only this client shows them). */
  clientToken: z.string().max(64).optional(),
});
export type RunCommandRequest = z.infer<typeof RunCommandRequest>;

/** A message only I see ("Only visible to you"): kept by my client, never stored. */
export const EphemeralMessage = z.object({
  id: Id,
  channelId: Id,
  threadRootId: Id.optional(),
  app: AppAuthor,
  text: z.string(),
  appBlocks: KitBlocks.optional(),
  createdAt: Timestamp,
});
export type EphemeralMessage = z.infer<typeof EphemeralMessage>;

/**
 * Run a command / press a button: what came back at once. Nothing when the app answered later (response_url) or
 * with an empty 200; `message` when it answered `in_channel`.
 */
export const AppInteractionResponse = z.object({
  ephemeral: EphemeralMessage.optional(),
  message: Message.optional(),
  /** A modal the app opened with this interaction's trigger_id before answering (also sent as `app.views`). */
  views: ViewStack.optional(),
});
export type AppInteractionResponse = z.infer<typeof AppInteractionResponse>;

/** POST /v1/w/:ws/messages/:messageId/actions — a Block Kit button was pressed. */
export const BlockActionRequest = z.object({
  actionId: z.string().min(1).max(255),
  blockId: z.string().max(255).optional(),
  value: z.string().max(2000).optional(),
  /** A select, picker, checkbox group, radio group or overflow menu: its new value (Slack's action fields). */
  selected: KitStateValue.optional(),
  /** Every element's value on the message (`state.values`). */
  values: KitStateValues.optional(),
  clientToken: z.string().max(64).optional(),
});
export type BlockActionRequest = z.infer<typeof BlockActionRequest>;

/**
 * What an app answers a slash command / button with, or POSTs to a response_url (Slack's fields). `text` or
 * `blocks`; `response_type` defaults to ephemeral. `replace_original` / `delete_original` act on the message whose
 * button was pressed (its app's messages only).
 */
export const AppMessagePayload = z.object({
  text: z.string().max(40_000).optional(),
  blocks: KitBlocks.optional(),
  response_type: z.enum(["ephemeral", "in_channel"]).optional(),
  replace_original: z.boolean().optional(),
  delete_original: z.boolean().optional(),
});
export type AppMessagePayload = z.infer<typeof AppMessagePayload>;

// ---------- OAuth install ----------

/** GET /v1/oauth/authorize?client_id&scope&redirect_uri — what the consent page shows. */
export const OAuthAuthorizeInfoResponse = z.object({
  app: z.object({ id: Id, name: z.string(), description: z.string().optional(), iconUrl: z.string().optional(), ownerWorkspaceName: z.string() }),
  scopes: z.array(AppScope),
  redirectUri: z.string(),
  /** Workspaces I can install into (I'm an owner or admin there). */
  workspaces: z.array(z.object({ id: Id, name: z.string() })),
});
export type OAuthAuthorizeInfoResponse = z.infer<typeof OAuthAuthorizeInfoResponse>;

/** POST /v1/oauth/authorize — "Allow": a one-time code (10 minutes) on the redirect URL. */
export const OAuthApproveRequest = z.object({
  clientId: z.string().min(1).max(100),
  /** Comma- or space-separated scopes, as the app asked. */
  scope: z.string().max(500),
  redirectUri: z.string().max(2048),
  state: z.string().max(1000).optional(),
  workspaceId: Id,
});
export type OAuthApproveRequest = z.infer<typeof OAuthApproveRequest>;

export const OAuthApproveResponse = z.object({ redirectUrl: z.string() });
export type OAuthApproveResponse = z.infer<typeof OAuthApproveResponse>;

// ---------- Outgoing webhooks ----------

export const MAX_OUTGOING_WEBHOOKS = 100;

export const OutgoingWebhook = z.object({
  id: Id,
  name: z.string().min(1).max(80),
  /** Channels it listens in (empty: every public channel, then a trigger word is required). */
  channelIds: z.array(Id),
  /** Only messages starting with one of these (case-insensitive); empty: every message in its channels. */
  triggerWords: z.array(z.string()),
  url: z.string(),
  /** HMAC key of X-Bundle-Signature (shown to who manages it). */
  signingSecret: z.string(),
  createdBy: Id,
  createdAt: Timestamp,
  lastUsedAt: Timestamp.optional(),
  /** The last delivery's failure ("HTTP 500", "timeout"), cleared by a success. */
  lastError: z.string().optional(),
});
export type OutgoingWebhook = z.infer<typeof OutgoingWebhook>;

export const ListOutgoingWebhooksResponse = z.object({ webhooks: z.array(OutgoingWebhook) });
export type ListOutgoingWebhooksResponse = z.infer<typeof ListOutgoingWebhooksResponse>;

const trigger = z.string().trim().min(1).max(50);

/** POST /v1/w/:ws/outgoing-webhooks — members; channels I'm in; a channel or a trigger word. */
export const CreateOutgoingWebhookRequest = z.object({
  name: z.string().trim().min(1).max(80),
  channelIds: z.array(Id).max(20),
  triggerWords: z.array(trigger).max(10),
  url,
});
export type CreateOutgoingWebhookRequest = z.infer<typeof CreateOutgoingWebhookRequest>;

export const UpdateOutgoingWebhookRequest = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  channelIds: z.array(Id).max(20).optional(),
  triggerWords: z.array(trigger).max(10).optional(),
  url: url.optional(),
});
export type UpdateOutgoingWebhookRequest = z.infer<typeof UpdateOutgoingWebhookRequest>;

export const OutgoingWebhookResponse = z.object({ webhook: OutgoingWebhook });
export type OutgoingWebhookResponse = z.infer<typeof OutgoingWebhookResponse>;
